/**
 * Shared API client. Creates axios instance with base URL and auth headers.
 * Used by api/*.js modules so slices/thunks only call API methods.
 */
import axios from "axios";
import store from "../store/store";
import { openRequireRefreshDialog } from "../slices/requireRefresh/requireRefreshSlice";
import { triggerVersionMismatch } from "../../context/appVersionEventBus";
import CustomerConfig from "../../azure/CustomerConfig";
import AuthTeams from "../../auth/AuthTeams";
import { logger } from "../../utils/logger";
import { createIfs401RetryHandler } from "./ifs401Retry";

/** User-Key: IFS code [, IFS email from ifsUserDetails] — see AuthTeams.getUserKey(). */
function getUserKeyHeader() {
  return new AuthTeams().getUserKey();
}

// 401 → fetchIfsToken → authService.startLogin({ silentOnly }) → getIfsToken; then retry once with fresh User-Key.
const handleIfs401Retry = createIfs401RetryHandler(getUserKeyHeader);

/**
 * @param {string} [accessToken] - Graph Bearer token; only for routes that call Graph/OneDrive.
 * @param {{ omitBearer?: boolean }} [options] - omitBearer: never send Authorization (see getIfsApiClient).
 * @returns {import('axios').AxiosInstance}
 */
export function getApiClient(accessToken, { omitBearer = false } = {}) {
  const baseURL = new CustomerConfig().getServiceApi();
  const client = axios.create({ baseURL, timeout: 60000 });
  if (accessToken && !omitBearer) {
    client.defaults.headers.common.authorization = `Bearer ${accessToken}`;
  }
  const auth = new AuthTeams();
  client.defaults.headers.common["Ocp-Apim-Subscription-Key"] =
    new CustomerConfig().getSubscriptionKey();
  client.defaults.headers.common["Customer-Key"] =
    auth.getCustomerKey() || process.env.REACT_APP_CLIENT_ID || "";
  client.defaults.headers.common["User-Key"] = getUserKeyHeader();
  client.defaults.headers.common["X-Client-Version"] =
    process.env.REACT_APP_VERSION ?? "0.0.0";

  client.interceptors.request.use(
    (config) => {
      // Always re-read User-Key (and Bearer from SSO cache when present).
      // Long-lived clients (chunked upload) otherwise keep a stale User-Key after IFS refresh.
      const HeadersCtor = axios.AxiosHeaders;
      const setHeader = (key, val) => {
        if (HeadersCtor && config.headers instanceof HeadersCtor) {
          config.headers.set(key, val);
        } else {
          config.headers = config.headers || {};
          config.headers[key] = val;
        }
      };
      setHeader("User-Key", getUserKeyHeader());
      const sso = omitBearer ? null : localStorage.getItem("ssoUserToken");
      if (sso) {
        setHeader("Authorization", `Bearer ${sso}`);
      }

      const method = (config.method || "get").toUpperCase();
      const url = config.url || "";
      const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url;
      logger.api(method, fullUrl, null, "API");
      return config;
    },
    (err) => {
      logger.error("API request failed", err, "API");
      return Promise.reject(err);
    }
  );

  client.interceptors.response.use(
    (response) => {
      logger.info(
        `API ${response.status} ${response.config?.method?.toUpperCase() || ""} ${response.config?.url || ""}`,
        null,
        "API"
      );
      return response;
    },
    async (err) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 400) {
        let msg = "";
        if (data) {
          if (typeof data === "string" && data.trim()) {
            msg = data;
          } else if (typeof data === "object") {
            msg = data.Exception || 
                  (data.Messages && Array.isArray(data.Messages) ? data.Messages.join(", ") : "") ||
                  data.message || 
                  data.detail || 
                  data.title || 
                  data.error || 
                  data.Error || 
                  "";
          }
        }
        if (msg) {
          err.message = msg;
        }
      }
      if (status === 426) {
        const serverVersion =
          data?.serverVersion ||
          err?.response?.headers?.["x-server-app-version"];
        triggerVersionMismatch(serverVersion);

        if (data?.requireRefresh) {
          store.dispatch(
            openRequireRefreshDialog({
              message: "Please click OK to continue.",
            })
          );
        }
        return new Promise(() => {});
      }
      const url = err?.config?.url || "";
      logger.error(
        `API error ${status ?? "network"} ${err?.config?.method?.toUpperCase() || ""} ${url}`,
        err,
        "API"
      );
      if (status === 401) {
        try {
          return await handleIfs401Retry(err);
        } catch (retryErr) {
          return Promise.reject(retryErr);
        }
      }
      return Promise.reject(err);
    }
  );

  return client;
}

/**
 * Client for IFS-only routes. The API resolves the IFS token from User-Key and
 * never reads the Graph Bearer on these routes, so no Authorization is sent.
 * @returns {import('axios').AxiosInstance}
 */
export function getIfsApiClient() {
  return getApiClient(undefined, { omitBearer: true });
}
